import {
  ApolloClient,
  HttpLink,
  InMemoryCache,
  from,
  split,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { createClient } from 'graphql-ws';
import { localStorageRemoveItem } from 'src/utils/localStorage';

import { STORAGE_KEYS } from '@constants/app';
import { ROUTES } from '@constants/routes';

import {
  sessionStorageGetItem,
  sessionStorageSetItem,
} from '@utils/sessionStorage';

import { TokenRefreshLink } from './refreshLink';
import { ROOT_FIELD_POLICY_CONFIG } from './root-field-policy';

const tokenRefreshLink = new TokenRefreshLink();
const isBrowserAvailable = typeof window !== 'undefined';
const sessionPath = sessionStorageGetItem('prevPath') || '';
const prevPatch = isBrowserAvailable
  ? window.location.pathname + window.location.search
  : '';

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (Array.isArray(graphQLErrors)) {
    graphQLErrors.forEach((error: Error) => {
      if (error.message === 'Unauthorized') {
        const savePatch = sessionPath || prevPatch;
        sessionStorageSetItem('prevPath', savePatch);
        localStorageRemoveItem(STORAGE_KEYS.REFRESH_GRAPHQL_TOKEN);
        localStorageRemoveItem(STORAGE_KEYS.ACCESS_GRAPHQL_TOKEN_EXPIRE);
        window.location.replace(ROUTES.SIGN_IN);
      }
    });
  }
  if (graphQLErrors && import.meta.env.DEV) {
    graphQLErrors.forEach(({ message, locations, path }) => {
      console.log(`[GraphQL error]: Message: ${message}}`);
      console.log('[GraphQL error]: Path:', path);
      console.log('[GraphQL error]: Locations: ', locations);
    });
  }

  if (networkError) console.error('[Network error]:', networkError);
});
const wsLink = new GraphQLWsLink(
  createClient({
    url: import.meta.env.APP_API_GRAPHQL_SUBSCRIPTION_ENDPOINT,
    keepAlive: 10_000,
    retryAttempts: 10,
    retryWait: async tryCount => {
      const delay = 30 * 1000; // 30 sec
      const attempt = tryCount + 1;
      await new Promise(resolve => setTimeout(resolve, delay * attempt));
    },
  }),
);

const httpLink = new HttpLink({
  uri: import.meta.env.APP_API_GRAPHQL_ENDPOINT,
  credentials: 'include',
});

const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription'
    );
  },
  wsLink,
  httpLink,
);

export const apolloClient = new ApolloClient({
  link: from([tokenRefreshLink, errorLink, splitLink]),
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: ROOT_FIELD_POLICY_CONFIG,
      },
    },
  }),
});
